import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import OrderFilters from './components/OrderFilters';
import OrderStatsPanel from './components/OrderStatsPanel';
import OrderTable from './components/OrderTable';
import OrderDetailsModal from './components/OrderDetailsModal';

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);

  // Mock user data
  const user = {
    id: 1,
    name: "Sarah Nakamura",
    email: "sarah.nakamura@techcompany.ug",
    role: "customer"
  };

  // Mock orders data
  const mockOrders = [
    {
      id: 1,
      orderNumber: "TT2024001",
      orderDate: "2024-09-15T10:30:00Z",
      status: "completed",
      paymentStatus: "paid",
      paymentMethod: "MTN Mobile Money",
      transactionId: "MM240915103045",
      paymentDate: "2024-09-15T10:35:00Z",
      totalAmount: 2500000,
      deliveryAddress: "Plot 15, Industrial Area, Kampala, Uganda",
      estimatedDelivery: "2024-09-20T09:00:00Z",
      trackingNumber: "TT240915001",
      isUrgent: false,
      items: [
        {
          name: "Network Infrastructure Assessment",
          category: "consultancy",
          description: "Comprehensive network audit and optimization recommendations",
          price: 1500000,
          quantity: 1
        },
        {
          name: "Firewall Configuration Service",
          category: "networking",
          description: "Enterprise firewall setup and security configuration",
          price: 1000000,
          quantity: 1
        }
      ],
      timeline: [
        {
          title: "Order Placed",
          description: "Order successfully submitted and payment confirmed",
          timestamp: "2024-09-15T10:30:00Z"
        },
        {
          title: "Payment Confirmed",
          description: "MTN Mobile Money payment processed successfully",
          timestamp: "2024-09-15T10:35:00Z"
        },
        {
          title: "Service Scheduled",
          description: "Technical team assigned and site visit scheduled",
          timestamp: "2024-09-16T08:00:00Z"
        },
        {
          title: "Service Completed",
          description: "All services delivered and documentation provided",
          timestamp: "2024-09-18T16:30:00Z"
        }
      ]
    },
    {
      id: 2,
      orderNumber: "TT2024002",
      orderDate: "2024-09-10T14:15:00Z",
      status: "in-progress",
      paymentStatus: "paid",
      paymentMethod: "Bank Transfer",
      transactionId: "BT240910141520",
      paymentDate: "2024-09-10T14:20:00Z",
      totalAmount: 5000000,
      deliveryAddress: "Makerere University, College of Computing, Kampala",
      estimatedDelivery: "2024-09-25T10:00:00Z",
      trackingNumber: "TT240910002",
      isUrgent: true,
      supportTicket: "SUP2024089",
      supportStatus: "Open",
      items: [
        {
          name: "Enterprise Software Development",
          category: "software",
          description: "Custom ERP system development for university management",
          price: 4000000,
          quantity: 1
        },
        {
          name: "Database Migration Service",
          category: "integration",
          description: "Legacy system data migration and integration",
          price: 1000000,
          quantity: 1
        }
      ],
      timeline: [
        {
          title: "Order Placed",
          description: "Order submitted with urgent priority flag",
          timestamp: "2024-09-10T14:15:00Z"
        },
        {
          title: "Payment Confirmed",
          description: "Bank transfer payment verified and processed",
          timestamp: "2024-09-10T14:20:00Z"
        },
        {
          title: "Development Started",
          description: "Project kickoff meeting completed, development phase initiated",
          timestamp: "2024-09-12T09:00:00Z"
        }
      ]
    },
    {
      id: 3,
      orderNumber: "TT2024003",
      orderDate: "2024-09-05T09:45:00Z",
      status: "pending",
      paymentStatus: "pending",
      paymentMethod: "Airtel Money",
      totalAmount: 750000,
      deliveryAddress: "Uganda Communications Commission, Bugolobi, Kampala",
      estimatedDelivery: "2024-09-22T14:00:00Z",
      isUrgent: false,
      items: [
        {
          name: "ICT Policy Review",
          category: "policy",
          description: "Review and update of organizational ICT policies",
          price: 500000,
          quantity: 1
        },
        {
          name: "Compliance Audit",
          category: "consultancy",
          description: "ICT compliance assessment and recommendations",
          price: 250000,
          quantity: 1
        }
      ],
      timeline: [
        {
          title: "Order Placed",
          description: "Order submitted and awaiting payment confirmation",
          timestamp: "2024-09-05T09:45:00Z"
        }
      ]
    },
    {
      id: 4,
      orderNumber: "TT2024004",
      orderDate: "2024-08-28T11:20:00Z",
      status: "completed",
      paymentStatus: "paid",
      paymentMethod: "Credit Card",
      transactionId: "CC240828112045",
      paymentDate: "2024-08-28T11:25:00Z",
      totalAmount: 1800000,
      deliveryAddress: "Ministry of ICT, Nakasero, Kampala, Uganda",
      estimatedDelivery: "2024-09-02T10:00:00Z",
      trackingNumber: "TT240828004",
      isUrgent: false,
      items: [
        {
          name: "Digital Transformation Strategy",
          category: "strategy",
          description: "Comprehensive digital transformation roadmap development",
          price: 1200000,
          quantity: 1
        },
        {
          name: "Change Management Training",
          category: "consultancy",
          description: "Staff training on digital transformation processes",
          price: 600000,
          quantity: 1
        }
      ],
      timeline: [
        {
          title: "Order Placed",
          description: "Order successfully submitted",
          timestamp: "2024-08-28T11:20:00Z"
        },
        {
          title: "Payment Confirmed",
          description: "Credit card payment processed successfully",
          timestamp: "2024-08-28T11:25:00Z"
        },
        {
          title: "Strategy Development",
          description: "Digital transformation strategy document completed",
          timestamp: "2024-08-30T16:00:00Z"
        },
        {
          title: "Training Delivered",
          description: "Change management training sessions completed",
          timestamp: "2024-09-02T15:30:00Z"
        }
      ]
    },
    {
      id: 5,
      orderNumber: "TT2024005",
      orderDate: "2024-08-20T16:30:00Z",
      status: "cancelled",
      paymentStatus: "refunded",
      paymentMethod: "MTN Mobile Money",
      transactionId: "MM240820163045",
      paymentDate: "2024-08-20T16:35:00Z",
      totalAmount: 300000,
      isUrgent: false,
      items: [
        {
          name: "Basic Website Development",
          category: "software",
          description: "Simple corporate website with CMS",
          price: 300000,
          quantity: 1
        }
      ],
      timeline: [
        {
          title: "Order Placed",
          description: "Order submitted and payment confirmed",
          timestamp: "2024-08-20T16:30:00Z"
        },
        {
          title: "Order Cancelled",
          description: "Order cancelled by customer request, refund processed",
          timestamp: "2024-08-21T10:15:00Z"
        }
      ]
    }
  ];

  useEffect(() => {
    // Simulate API call
    const loadOrders = async () => {
      setIsLoading(true);
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      setOrders(mockOrders);
      setFilteredOrders(mockOrders);
      setIsLoading(false);
    };

    loadOrders();
  }, []);

  const handleFiltersChange = (filters) => {
    let filtered = [...orders];

    // Search filter
    if (filters?.searchQuery) {
      filtered = filtered?.filter(order =>
        order?.orderNumber?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase()) ||
        order?.items?.some(item => 
          item?.name?.toLowerCase()?.includes(filters?.searchQuery?.toLowerCase())
        )
      );
    }

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(order => order?.status === filters?.status);
    }

    // Payment status filter
    if (filters?.paymentStatus !== 'all') {
      filtered = filtered?.filter(order => order?.paymentStatus === filters?.paymentStatus);
    }

    // Service category filter
    if (filters?.serviceCategory !== 'all') {
      filtered = filtered?.filter(order =>
        order?.items?.some(item => item?.category === filters?.serviceCategory)
      );
    }

    // Payment method filter
    if (filters?.paymentMethod !== 'all') {
      filtered = filtered?.filter(order => 
        order?.paymentMethod?.toLowerCase()?.includes(filters?.paymentMethod?.toLowerCase())
      );
    }

    // Date range filter
    if (filters?.dateRange !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (filters?.dateRange) {
        case 'today':
          filterDate?.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate?.setDate(now?.getDate() - 7);
          break;
        case 'month':
          filterDate?.setMonth(now?.getMonth() - 1);
          break;
        case 'quarter':
          filterDate?.setMonth(now?.getMonth() - 3);
          break;
        case 'year':
          filterDate?.setFullYear(now?.getFullYear() - 1);
          break;
        default:
          break;
      }
      
      if (filters?.dateRange !== 'all') {
        filtered = filtered?.filter(order => 
          new Date(order.orderDate) >= filterDate
        );
      }
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setIsDetailsModalOpen(true);
  };

  const handleDownloadInvoice = (order) => {
    // Mock invoice download
    const invoiceData = {
      orderNumber: order?.orderNumber,
      customerName: user?.name,
      items: order?.items,
      total: order?.totalAmount,
      date: order?.orderDate
    };
    
    console.log('Downloading invoice for order:', invoiceData);
    
    // Create mock PDF download
    const element = document.createElement('a');
    const file = new Blob([`Invoice for Order ${order.orderNumber}\nTotal: UGX ${order.totalAmount.toLocaleString()}`], 
      { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `invoice-${order?.orderNumber}.txt`;
    document.body?.appendChild(element);
    element?.click();
    document.body?.removeChild(element);
  };

  const handleTrackOrder = (order) => {
    // Mock tracking functionality
    alert(`Tracking order ${order?.orderNumber}\nStatus: ${order?.status}\nTracking Number: ${order?.trackingNumber || 'N/A'}`);
  };

  const handleReorder = (order) => {
    // Mock reorder functionality
    const reorderItems = order?.items?.map(item => ({
      ...item,
      id: Date.now() + Math.random()
    }));
    
    console.log('Reordering items:', reorderItems);
    alert(`Items from order ${order?.orderNumber} have been added to your cart!`);
  };

  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders?.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders?.length / ordersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation user={user} />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading your order history...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation user={user} />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Order History</h1>
              <p className="text-muted-foreground mt-2">
                Track and manage your ICT solution orders and implementations
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/product-catalog">
                <Button variant="outline" iconName="Plus" iconPosition="left">
                  New Order
                </Button>
              </Link>
              <Link to="/rfq-request-form">
                <Button variant="default" iconName="FileText" iconPosition="left">
                  Request Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Order Statistics */}
          <OrderStatsPanel orders={orders} />

          {/* Filters */}
          <OrderFilters 
            onFiltersChange={handleFiltersChange}
            totalOrders={filteredOrders?.length}
          />

          {/* Orders Table */}
          <OrderTable
            orders={currentOrders}
            onViewDetails={handleViewDetails}
            onDownloadInvoice={handleDownloadInvoice}
            onTrackOrder={handleTrackOrder}
            onReorder={handleReorder}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-muted-foreground">
                Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, filteredOrders?.length)} of {filteredOrders?.length} orders
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  iconName="ChevronLeft"
                />
                {Array.from({ length: totalPages }, (_, i) => i + 1)?.map((number) => (
                  <Button
                    key={number}
                    variant={currentPage === number ? "default" : "outline"}
                    size="sm"
                    onClick={() => paginate(number)}
                  >
                    {number}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  iconName="ChevronRight"
                />
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="mt-12 bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-6 border border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Need Help with Your Orders?
                </h3>
                <p className="text-muted-foreground">
                  Our support team is here to assist you with any questions about your orders or services.
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Button variant="outline" iconName="MessageCircle" iconPosition="left">
                  Contact Support
                </Button>
                <Button variant="default" iconName="Phone" iconPosition="left">
                  Call Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        onDownloadInvoice={handleDownloadInvoice}
        onTrackOrder={handleTrackOrder}
      />
    </div>
  );
};

export default OrderHistory;